import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TOUCH_TARGET_PX } from '../../config/touch-target';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { isImageDocumentFilename } from '../../utils/document-file.utils';
import { truncateFileName } from '../../utils/truncate-file-name';

/** Characters of the file name shown before the middle cut: the pill fills the phone width. */
const MOBILE_NAME_MAX_CHARS = 28;
const DESKTOP_NAME_MAX_CHARS = 40;
const LEADING_ICON_PX = 20;
/** Same radius as the theme chips: the pill reads as a chip while keeping a 48px remove button. */
const PILL_RADIUS_PX = 20;

interface DocumentFilePillProps {
  file: File;
  /** Upload or reading in progress: a spinner takes the place of the file type icon. */
  isBusy: boolean;
  disabled: boolean;
  /** The field label names the group for screen readers. */
  labelId: string;
  testIdPrefix: string;
  onRemove: () => void;
}

/** A file adopted from the assistant has no MIME type: the extension tells PDF from photo. */
function FileTypeIcon({ file }: { file: File }) {
  const iconSx = { color: 'text.secondary', fontSize: LEADING_ICON_PX } as const;
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    return <PictureAsPdfOutlinedIcon aria-hidden sx={iconSx} />;
  }
  if (file.type.startsWith('image/') || isImageDocumentFilename(file.name)) {
    return <ImageOutlinedIcon aria-hidden sx={iconSx} />;
  }
  return <InsertDriveFileOutlinedIcon aria-hidden sx={iconSx} />;
}

/** The chosen document in place of the upload buttons: its name, its state and the X to remove it. */
export function DocumentFilePill({
  file,
  isBusy,
  disabled,
  labelId,
  testIdPrefix,
  onRemove,
}: DocumentFilePillProps) {
  const { t } = useTranslation('documents');
  const isMobile = useIsMobile();
  const maxChars = isMobile ? MOBILE_NAME_MAX_CHARS : DESKTOP_NAME_MAX_CHARS;
  return (
    <Box
      role="group"
      aria-labelledby={labelId}
      data-testid={`${testIdPrefix}-file-name`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: TOUCH_TARGET_PX,
        width: { xs: '100%', sm: 'fit-content' },
        maxWidth: '100%',
        minWidth: 0,
        pl: 1.5,
        borderRadius: `${PILL_RADIUS_PX}px`,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {isBusy ? (
        <CircularProgress
          size={LEADING_ICON_PX}
          aria-hidden
          data-testid={`${testIdPrefix}-busy`}
          sx={{ flexShrink: 0 }}
        />
      ) : (
        <FileTypeIcon file={file} />
      )}
      <Typography
        component="bdi"
        variant="body2"
        title={file.name}
        sx={{
          flex: 1,
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: 500,
        }}
      >
        {truncateFileName(file.name, maxChars)}
      </Typography>
      <IconButton
        onClick={onRemove}
        disabled={disabled}
        aria-label={t('deadlines.aiPreview.removeFileNamed', {
          name: file.name,
          defaultValue: 'Togli il documento {{name}}',
        })}
        data-testid={`${testIdPrefix}-remove`}
        sx={{
          width: TOUCH_TARGET_PX,
          height: TOUCH_TARGET_PX,
          flexShrink: 0,
          color: 'text.secondary',
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
